import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Search,
  CheckCircle2,
  Truck,
  Clock,
  XCircle,
  MessageCircle,
  Package,
  MapPin,
  Calendar,
  ExternalLink,
  Mail,
} from "lucide-react";
import { api, formatINR, BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";

const STATUS_FLOW = ["pending_payment", "paid", "shipped", "delivered"];
const STATUS_LABELS = {
  pending_payment: "Payment Pending",
  paid: "Confirmed",
  shipped: "Shipped",
  delivered: "Delivered",
  cancelled: "Cancelled",
};

const absoluteUrl = (u) => {
  if (!u) return "";
  if (u.startsWith("http") || u.startsWith("data:")) return u;
  return `${BACKEND_URL}${u}`;
};

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso?.slice(0, 10) || "";
  }
}

export default function TrackOrder() {
  const [params, setParams] = useSearchParams();
  const [mode, setMode] = useState("id"); // "id" | "email"
  const [orderId, setOrderId] = useState(params.get("id") || "");
  const [email, setEmail] = useState(params.get("email") || "");
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const lookup = async (overrides = {}) => {
    const oid = (overrides.id ?? orderId).trim();
    const em = (overrides.email ?? email).trim();
    if (!oid && !em) return toast.error("Please enter your order ID or email");
    setLoading(true);
    setSearched(true);
    try {
      const { data } = await api.post("/orders/lookup", {
        order_id: oid || null,
        email: em || null,
      });
      setOrder(data);
    } catch (e) {
      setOrder(null);
      if (e?.response?.status === 404) {
        toast.error(e.response.data?.detail || "Order not found");
      } else {
        toast.error("Could not load order. Try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (params.get("id") || params.get("email")) {
      lookup({ id: params.get("id") || "", email: params.get("email") || "" });
    }
    // eslint-disable-next-line
  }, []);

  const submit = (e) => {
    e?.preventDefault?.();
    if (mode === "id") setParams(orderId.trim() ? { id: orderId.trim() } : {});
    else setParams(email.trim() ? { email: email.trim() } : {});
    lookup();
  };

  return (
    <div className="pt-28 md:pt-32 pb-20 min-h-screen bg-white" data-testid="page-track-order">
      <div className="max-w-3xl mx-auto px-5 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-10"
        >
          <div className="overline mb-3 text-[#C9A961]">Order tracking</div>
          <h1 className="font-serif-display text-4xl md:text-6xl tracking-tighter font-light leading-[1.05]">
            Where's my <em className="gold-text not-italic">ritual?</em>
          </h1>
          <p className="text-sm text-[#5C5C5C] mt-4 max-w-md mx-auto">
            Track by order ID (starts with <span className="font-mono">BL</span>) — or look up your most recent order by email.
          </p>
        </motion.div>

        {/* Mode toggle */}
        <div className="flex items-center justify-center gap-1 mb-5" data-testid="track-mode-toggle">
          {[
            { key: "id", label: "Order ID", icon: Package },
            { key: "email", label: "Email", icon: Mail },
          ].map((m) => (
            <button
              key={m.key}
              onClick={() => setMode(m.key)}
              className={`flex items-center gap-2 px-4 py-2 text-[11px] tracking-[0.22em] uppercase border-b-2 -mb-px transition-colors ${
                mode === m.key
                  ? "border-[#C9A961] text-black"
                  : "border-transparent text-[#5C5C5C] hover:text-black"
              }`}
              data-testid={`track-mode-${m.key}`}
            >
              <m.icon size={13} strokeWidth={1.5} /> {m.label}
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="flex items-stretch gap-2 mb-8" data-testid="track-form">
          {mode === "id" ? (
            <input
              type="text"
              value={orderId}
              onChange={(e) => setOrderId(e.target.value.toUpperCase())}
              placeholder="BLxxxxxxxxxx"
              className="flex-1 bg-transparent border border-black/15 focus:border-[#C9A961] outline-none px-4 py-3 text-sm font-mono uppercase tracking-wider"
              data-testid="track-input"
            />
          ) : (
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="flex-1 bg-transparent border border-black/15 focus:border-[#C9A961] outline-none px-4 py-3 text-sm"
              data-testid="track-email-input"
            />
          )}
          <button
            type="submit"
            disabled={loading}
            className="px-5 bg-black text-white text-[11px] tracking-[0.22em] uppercase hover:bg-[#C9A961] transition-colors disabled:opacity-50"
            data-testid="track-submit"
          >
            {loading ? "…" : <Search size={14} />}
          </button>
        </form>

        {searched && !loading && !order && (
          <div className="border border-dashed border-black/15 p-10 text-center" data-testid="track-empty">
            <XCircle size={28} strokeWidth={1.2} className="text-[#5C5C5C] mx-auto mb-3" />
            <div className="font-serif-display text-2xl mb-2">Order not found</div>
            <p className="text-sm text-[#5C5C5C] mb-5">
              Double-check the details, or message us — we'll find it for you.
            </p>
            <a
              href="https://wa.me/917383899003"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-outline inline-flex"
              data-testid="track-empty-whatsapp"
            >
              <MessageCircle size={14} /> WhatsApp Support
            </a>
          </div>
        )}

        {order && <OrderCard order={order} />}
      </div>
    </div>
  );
}

function OrderCard({ order }) {
  const items = Array.isArray(order.items) ? order.items : [];
  const eta = order.estimated_delivery;
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="border border-black/10"
      data-testid="track-result"
    >
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3 px-5 md:px-7 py-5 border-b border-black/5">
        <div>
          <div className="overline mb-1">Order</div>
          <div className="font-mono text-lg" data-testid="track-order-id">{order.order_id}</div>
          <div className="text-[11px] text-[#5C5C5C] mt-0.5">Placed {formatDate(order.created_at)}</div>
        </div>
        <div className="text-right">
          <div className="overline text-[10px] mb-1">Total</div>
          <div className="font-serif-display text-2xl">{formatINR(order.total)}</div>
        </div>
      </div>

      {/* ETA banner */}
      {eta && order.status !== "cancelled" && order.status !== "delivered" && (
        <div className="flex items-center gap-3 px-5 md:px-7 py-4 bg-[#F8F5F0]/60 border-b border-black/5" data-testid="track-eta">
          <Calendar size={18} strokeWidth={1.4} className="text-[#C9A961] shrink-0" />
          <div className="text-sm">
            <span className="text-[#5C5C5C]">Estimated delivery:</span>{" "}
            <span className="font-medium" data-testid="track-eta-date">{formatDate(eta)}</span>
          </div>
        </div>
      )}
      {order.status === "delivered" && (
        <div className="flex items-center gap-3 px-5 md:px-7 py-4 bg-green-50/60 border-b border-black/5" data-testid="track-delivered">
          <CheckCircle2 size={18} strokeWidth={1.6} className="text-green-700 shrink-0" />
          <div className="text-sm font-medium text-green-800">
            Delivered on {formatDate(order.delivered_at || order.updated_at)}
          </div>
        </div>
      )}

      <div className="px-5 md:px-7 py-6">
        {/* Timeline */}
        <ol className="space-y-4" data-testid="track-timeline">
          {STATUS_FLOW.map((step, idx) => {
            const currentIdx = STATUS_FLOW.indexOf(order.status);
            const done = currentIdx >= idx;
            const active = order.status === step;
            const Icon = step === "shipped" ? Truck : step === "delivered" ? CheckCircle2 : step === "paid" ? CheckCircle2 : Clock;
            return (
              <li key={step} className="flex items-start gap-4">
                <div
                  className={`w-9 h-9 rounded-full border-2 flex items-center justify-center shrink-0 ${
                    done ? "border-[#C9A961] bg-[#C9A961] text-white" : "border-black/15 text-[#B8B8B8]"
                  }`}
                >
                  <Icon size={14} strokeWidth={1.6} />
                </div>
                <div className={`flex-1 pt-1.5 ${active ? "font-medium" : ""}`}>
                  <div className={`text-sm ${done ? "text-black" : "text-[#B8B8B8]"}`}>{STATUS_LABELS[step]}</div>
                  {active && <div className="text-xs text-[#C9A961] uppercase tracking-wider mt-0.5">Current status</div>}
                </div>
              </li>
            );
          })}
        </ol>

        {order.status === "cancelled" && (
          <div className="mt-6 p-4 bg-red-50/50 text-sm text-red-700">
            This order was cancelled. Contact us if this is unexpected.
          </div>
        )}

        {/* Courier / Tracking link */}
        {(order.tracking_number || order.tracking_url || order.courier) && (
          <div className="mt-6 border border-[#C9A961]/30 bg-[#C9A961]/5 p-4" data-testid="track-courier-block">
            <div className="overline text-[10px] text-[#C9A961] mb-2">Courier</div>
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="text-sm">
                {order.courier && (
                  <span className="font-medium uppercase text-xs tracking-wider mr-2" data-testid="track-courier-name">
                    {order.courier}
                  </span>
                )}
                {order.tracking_number && (
                  <span className="font-mono text-[13px]" data-testid="track-courier-tn">
                    {order.tracking_number}
                  </span>
                )}
              </div>
              {order.tracking_url && (
                <a
                  href={order.tracking_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[11px] tracking-[0.22em] uppercase flex items-center gap-1 text-black hover:text-[#C9A961] transition-colors"
                  data-testid="track-courier-link"
                >
                  Live tracking <ExternalLink size={11} />
                </a>
              )}
            </div>
          </div>
        )}

        {/* Items list */}
        {items.length > 0 && (
          <div className="mt-7 pt-5 border-t border-black/10" data-testid="track-items">
            <div className="overline mb-3">Items ({items.length})</div>
            <ul className="space-y-3">
              {items.map((it, i) => (
                <li
                  key={`${it.product_id || i}-${i}`}
                  className="flex items-center gap-3"
                  data-testid={`track-item-${i}`}
                >
                  <Link
                    to={it.product_id ? `/product/${it.product_id}` : "/shop"}
                    className="w-14 h-14 bg-[#F8F5F0] overflow-hidden flex-shrink-0 border border-black/5"
                  >
                    {it.image ? (
                      <img
                        src={absoluteUrl(it.image)}
                        alt={it.name}
                        className="w-full h-full object-cover"
                        loading="lazy"
                        decoding="async"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Package size={16} className="text-[#B8B8B8]" />
                      </div>
                    )}
                  </Link>
                  <div className="flex-1 min-w-0">
                    <Link
                      to={it.product_id ? `/product/${it.product_id}` : "/shop"}
                      className="text-sm hover:text-[#C9A961] transition-colors block truncate"
                    >
                      {it.name || "Product"}
                    </Link>
                    <div className="text-xs text-[#5C5C5C] mt-0.5">
                      Qty {it.quantity} · {formatINR(it.price)} ea.
                    </div>
                  </div>
                  <div className="text-sm font-medium shrink-0">{formatINR(it.line_total || it.price * it.quantity)}</div>
                </li>
              ))}
            </ul>

            {/* Totals */}
            <div className="mt-4 pt-4 border-t border-black/5 space-y-1 text-sm">
              <div className="flex justify-between text-[#5C5C5C]">
                <span>Subtotal</span>
                <span>{formatINR(order.subtotal)}</span>
              </div>
              <div className="flex justify-between text-[#5C5C5C]">
                <span>Shipping{order.state ? ` · ${order.state}` : ""}</span>
                <span>{order.shipping > 0 ? formatINR(order.shipping) : "FREE"}</span>
              </div>
              {order.coupon_discount > 0 && (
                <div className="flex justify-between text-[#C9A961]">
                  <span>Discount{order.coupon_code ? ` (${order.coupon_code})` : ""}</span>
                  <span>− {formatINR(order.coupon_discount)}</span>
                </div>
              )}
              <div className="flex justify-between font-medium pt-1">
                <span>Total</span>
                <span>{formatINR(order.total)}</span>
              </div>
            </div>
          </div>
        )}

        {/* Shipping address */}
        {(order.address || order.city) && (
          <div className="mt-6 pt-5 border-t border-black/10" data-testid="track-address">
            <div className="overline mb-2 flex items-center gap-1.5">
              <MapPin size={11} strokeWidth={1.5} className="text-[#C9A961]" /> Shipping address
            </div>
            <div className="text-sm leading-relaxed">
              <div className="font-medium">{order.full_name}</div>
              <div className="text-[#5C5C5C]">
                {order.address}
                {order.city ? `, ${order.city}` : ""}
                {order.state ? `, ${order.state}` : ""} {order.postal_code || ""}
              </div>
              <div className="text-[#5C5C5C] text-xs mt-1">{order.phone}</div>
            </div>
          </div>
        )}

        <a
          href={`https://wa.me/917383899003?text=Hi%20Blisson%2C%20I%27d%20like%20an%20update%20on%20order%20${encodeURIComponent(order.order_id)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-outline w-full mt-6 justify-center"
          data-testid="track-whatsapp"
        >
          <MessageCircle size={14} /> Need help with this order?
        </a>
      </div>
    </motion.div>
  );
}
